<template>
  <SBaseInput
    :ref="container.ref"
    v-bind="pickProps(props, useBaseInput.scoped())"
    class="s-select"
    :class="{ 's-select--is-open': isOpen }"
    :label-id="labelledBy.ids.label.value"
    v-on="mergeListeners(reEmit(emit, useBaseInput.emits), container.on)"
  >
    <template #input="{ aria, validation }">
      <SButton
        :ref="comboboxAtom.ref"
        v-bind="mergeReactive(button.props, comboboxAtom.props, aria)"
        :aria-labelledby="labelledBy.aria.value"
        class="s-select__combobox"
        fluid
        :invalid="validation.props.invalid"
        tag="span"
        v-on="mergeListeners(comboboxAtom.on, validation.on)"
      >
        <template #default>
          <template v-if="!multiValueDisplayAtom.if">
            {{ comboboxText }}
          </template>
          <template v-else>
            {{ placeholder }}
            <span class="s-select__count-badge">{{ selectedCount }}</span>
          </template>
        </template>
        <template #end>
          <i class="s-select__chevron fa-regular fa-chevron-down" />
        </template>
      </SButton>

      <STransition name="fade">
        <keep-alive>
          <slot v-if="isOpen" name="listbox" v-bind="listboxAtom">
            <SListbox
              v-if="isOpen"
              :ref="listboxAtom.ref"
              v-bind="listboxAtom.props"
              v-on="listboxAtom.on"
            >
              <template v-if="multiValueDisplayAtom.if" #footer>
                <button
                  class="s-select__clear-btn"
                  type="button"
                  @click.stop="clearAll"
                  @mousedown.stop
                >
                  {{ clearLabel }}
                </button>
              </template>
            </SListbox>
          </slot>
        </keep-alive>
      </STransition>
    </template>
    <template v-for="(_index, slotName) in $slots" #[slotName]="data">
      <slot :name="slotName" v-bind="data" />
    </template>
  </SBaseInput>
</template>

<script setup lang="ts">
import type { UseButtonStyleProps } from "@/lib/components/logic/atoms/useButton.ts";
import type {
  UseBaseInputEmit,
  UseBaseInputScoped,
} from "@/lib/components/logic/molecules/useBaseInput.ts";
import type {
  UseSelectEmit,
  UseSelectProps,
} from "@/lib/components/logic/molecules/useSelect";

import SButton from "@/lib/components/atoms/button/SButton.vue";
import SListbox from "@/lib/components/atoms/listbox/SListbox.vue";
import STransition from "@/lib/components/behaviour/STransition.vue";
import * as useButton from "@/lib/components/logic/atoms/useButton.ts";
import * as useBaseInput from "@/lib/components/logic/molecules/useBaseInput.ts";
import * as useSelect from "@/lib/components/logic/molecules/useSelect";
import SBaseInput from "@/lib/components/molecules/SBaseInput.vue";
import { pickProps, reEmit } from "@/lib/composables/componentComposable.ts";
import { useAriaIds } from "@/lib/composables/useAriaIds.ts";
import { mergeListeners, mergeReactive } from "@/lib/helpers/reactivity.ts";

const props = withDefaults(
  defineProps<
    UseBaseInputScoped<UseSelectProps["modelValue"]> &
      UseButtonStyleProps &
      UseSelectProps
  >(),
  {
    actionType: "tertiary-soft",
    color: "gray",
    opaque: true,
    textAlign: "left",
  },
);

const emit = defineEmits<UseBaseInputEmit & UseSelectEmit>();

const {
  container,
  comboboxAtom,
  listboxAtom,
  isOpen,
  multiValueDisplayAtom,
  comboboxText,
  placeholder,
  selectedCount,
  clearAll,
  clearLabel,
} = useSelect.use(props, emit);

const button = { props: pickProps(props, useButton.styleProps) };

const labelledBy = useAriaIds(
  { label: () => props.label },
  () => props.ariaLabelledby,
);
</script>

<style lang="postcss">
.s-select {
  @apply max-w-full;

  &__chevron {
    @apply transition-transform;
  }

  &--is-open .s-select__chevron {
    transform: rotate(180deg);
  }

  &__clear-btn {
    @apply w-full cursor-pointer border-t border-gray-100 px-3 py-2 text-center text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-danger-700;
  }

  &__count-badge {
    @apply inline-flex h-4 min-w-[1rem] items-center justify-center rounded bg-slate-600 px-1 text-xs font-bold leading-none text-white;
  }

  .s-input-wrapper-atom--enabled {
    @apply cursor-pointer;
  }

  .s-input-icon-atom--success {
    @apply text-primary-600;
  }
}
</style>
